/**
 * Починка Ogg Opus с позициями не в тех единицах.
 *
 * По спецификации (RFC 7845) позиция страницы — число отсчётов на 48 кГц, что бы
 * ни стояло в заголовке как исходная частота. Встречаются файлы, где её считали
 * на исходной (44,1 кГц: 882 на пакет в 20 мс вместо 960). Chromium такое
 * прощает, Firefox — нет: и `decodeAudioData`, и `<audio>` отказываются разбирать
 * поток. Здесь позиции пересчитываются по длительности пакетов, а контрольные
 * суммы страниц — заново.
 */

const RATE = 48_000;

interface Page {
  start: number;
  /** Где начинается тело — за заголовком и таблицей отрезков. */
  body: number;
  /** Длина страницы целиком. */
  length: number;
  headerType: number;
  granule: bigint;
  /** Первые байты пакетов, которые на этой странице заканчиваются, — по ним считается длительность. */
  completed: Uint8Array[];
}

function readPages(bytes: Uint8Array): Page[] | null {
  const pages: Page[] = [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let at = 0;
  // Пакет, начатый на прошлой странице: его первые байты остались там.
  let pending: Uint8Array | null = null;
  while (at < bytes.length) {
    if (at + 27 > bytes.length || view.getUint32(at) !== 0x4f676753 /* OggS */) return null;
    const segments = bytes[at + 26];
    const body = at + 27 + segments;
    let cursor = body;
    const completed: Uint8Array[] = [];
    for (const lace of bytes.subarray(at + 27, body)) {
      pending ??= bytes.subarray(cursor, cursor + 2);
      cursor += lace;
      if (lace < 255) {
        completed.push(pending);
        pending = null;
      }
    }
    if (cursor > bytes.length) return null;
    pages.push({
      start: at,
      body,
      length: cursor - at,
      headerType: bytes[at + 5],
      granule: view.getBigInt64(at + 6, true),
      completed,
    });
    at = cursor;
  }
  return pages;
}

/** Отсчётов на 48 кГц в пакете Opus — по его первому байту (TOC, RFC 6716). */
export function opusSamples(head: Uint8Array): number | null {
  if (head.length === 0) return null;
  const toc = head[0];
  const config = toc >> 3;
  const frame =
    config < 12
      ? [480, 960, 1920, 2880][config % 4]
      : config < 16
        ? [480, 960][config % 2]
        : [120, 240, 480, 960][config % 4];
  const code = toc & 3;
  if (code === 0) return frame;
  if (code === 1 || code === 2) return frame * 2;
  if (head.length < 2) return null;
  return frame * (head[1] & 0x3f);
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let crc = i << 24;
    for (let bit = 0; bit < 8; bit++) crc = crc & 0x80000000 ? (crc << 1) ^ 0x04c11db7 : crc << 1;
    table[i] = crc >>> 0;
  }
  return table;
})();

/** Контрольная сумма страницы Ogg — по странице с обнулённым полем суммы. */
export function oggCrc(page: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < page.length; i++) {
    const byte = i >= 22 && i < 26 ? 0 : page[i];
    crc = ((crc << 8) ^ CRC_TABLE[((crc >>> 24) ^ byte) & 0xff]) >>> 0;
  }
  return crc;
}

/**
 * Поток с исправленными позициями — или `null`, если это не Ogg Opus, разобрать
 * его не вышло или чинить нечего. Обрезанный хвост (позиция последней страницы
 * меньше набранного) сохраняется в пересчёте на 48 кГц.
 */
export function repairOggOpus(bytes: Uint8Array): Uint8Array<ArrayBuffer> | null {
  const pages = readPages(bytes);
  if (!pages || pages.length < 3) return null;
  const first = pages[0];
  const head = bytes.subarray(first.body, first.start + first.length);
  // Заголовок: «OpusHead», версия, каналы, pre-skip (2), исходная частота (4).
  if (head.length < 16 || String.fromCharCode(...head.subarray(0, 8)) !== "OpusHead") return null;
  const inputRate = new DataView(head.buffer, head.byteOffset + 12, 4).getUint32(0, true);

  // Своя копия на обычном ArrayBuffer: её отдают в Blob и File.
  const fixed = new Uint8Array(bytes);
  const view = new DataView(fixed.buffer);
  let headers = 0;
  let total = 0n;
  let changed = false;
  // Позиции в исходной частоте, а не в 48 кГц? Видно по первой странице со звуком.
  // Нет — поток не трогаем вовсе: мелкие расхождения на единицу Firefox терпит.
  let foreign: boolean | null = null;
  const scale = (granule: bigint) =>
    inputRate > 0 ? (granule * BigInt(RATE) + BigInt(inputRate) / 2n) / BigInt(inputRate) : granule;
  const last = pages.length - 1;
  for (const [index, page] of pages.entries()) {
    let granule: bigint;
    if (headers < 2) {
      // Страницы заголовка и тегов — позиция 0. Аудио на одной странице с ними
      // спецификация запрещает: такой поток не трогаем.
      headers += page.completed.length;
      if (headers > 2) return null;
      granule = 0n;
    } else if (page.completed.length === 0) {
      // Пустая последняя страница несёт конец звука; прочие без пакетов — -1.
      granule = index === last && page.granule >= 0n ? (scale(page.granule) < total ? scale(page.granule) : total) : -1n;
    } else {
      for (const packet of page.completed) {
        const samples = opusSamples(packet);
        if (samples === null) return null;
        total += BigInt(samples);
      }
      if (foreign === null) {
        const expected = (total * BigInt(inputRate)) / BigInt(RATE);
        const off = page.granule - expected;
        foreign = inputRate !== RATE && page.granule !== total && off >= -1n && off <= 1n;
        if (!foreign) return null;
      }
      granule = total;
      // Хвост обрезают позицией последней страницы — в тех же единицах, что и всё остальное.
      if (index === last && page.granule >= 0n && scale(page.granule) < total) granule = scale(page.granule);
    }
    if (granule !== page.granule) {
      changed = true;
      view.setBigInt64(page.start + 6, granule, true);
    }
  }
  if (!changed) return null;

  for (const page of pages) {
    view.setUint32(page.start + 22, oggCrc(fixed.subarray(page.start, page.start + page.length)), true);
  }
  return fixed;
}
